import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { useI18n, useSelector, useStaffCan, useLogger } from '~Hooks';
import { Wrapper } from '~Scenes/Zreports/View/Styles';
import Loader from '~ComponentsImproved/Loader';
import { Box } from '~Styles';
import { useStorage } from '~Components/Storage';
import { DateTime } from 'luxon';
import { useReport } from '~Scenes/Reports/Hooks/useReport';
import { GetCategoriesQuery_user_company_categories } from '~Scenes/Reports/__queries__';
import ReportView from '~Components/Reports';
import { CountAndAmount } from '@baemingo/report-events/dist/types';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { ReportDataType } from '@baemingo/reports-builder';

interface Props {
  allCategories: GetCategoriesQuery_user_company_categories[];
  /** We use the same value as in the Calendar for the general report */
  chosenDate: Date;
  /** Optional if the admin user is viewing a specific staff report */
  selectedStaffId?: string | null;
  openCheques: CountAndAmount | null;
}

const Report: FC<Props> = ({ allCategories, chosenDate, selectedStaffId, openCheques }) => {
  const [, , venueId] = location.hash.split('/');
  const { i18n } = useI18n();
  const logger = useLogger('report');
  const [storage] = useStorage();
  const staffs = useSelector((state) => state.companyStaff?.staffs);
  const staffCan = useStaffCan();
  const reportGenerator = useReport(venueId, chosenDate);
  const [report, setReport] = useState<ReportDataType | null>(null);

  const staffToView = staffs?.find((s) => s.id === selectedStaffId);

  useEffect(() => {
    const fetchReport = async () => {
      const generatedReport = await reportGenerator.generateReport({
        isStaffReport: !!selectedStaffId,
      });

      if (selectedStaffId) {
        const foundStaffReport =
          generatedReport && generatedReport[selectedStaffId ?? '']
            ? generatedReport[selectedStaffId ?? '']
            : null;
        setReport(foundStaffReport);
      } else {
        setReport(generatedReport);
      }
    };

    fetchReport();
  }, [selectedStaffId, chosenDate]);

  const reportData = report
    ? reportGenerator.formatGeneratedReport({
        report,
        showCategorySales: staffCan.REPORTS || storage.showDetailedSalesBreakdown,
        allCategories,
        selectedStaffId: selectedStaffId ?? undefined,
        openCheques,
      })
    : null;

  const isReportEmpty =
    report?.totalSales === 0 &&
    report?.totalRefunds.amount === 0 &&
    report?.discountsAmount === 0 &&
    report?.totalTips === 0 &&
    Object.keys((report as any)?.removalDetails || {}).length === 0;

  useEffect(() => {
    if (isReportEmpty && !reportGenerator.loading) {
      logger.info('Report is empty', {
        staffToView,
        date: chosenDate,
      });
    }
  }, [chosenDate, selectedStaffId, staffToView, reportGenerator.loading]);

  const isHandyBreakpoint = window.innerWidth <= 600;

  if (reportGenerator.loading || reportGenerator.reportLoading) {
    return (
      <Box style={{ height: '50vh' }} display="flex" alignItems="center" justifyContent="center">
        <Loader color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'} />
      </Box>
    );
  }

  const reportDate = DateTime.fromJSDate(chosenDate).toFormat('yyyy–MM–dd');
  const reportTitle = staffToView ? staffToView?.name ?? '' : i18n('Report.FullReport');

  return (
    <Wrapper>
      {isReportEmpty ? (
        <Box marginTop="small">
          <Heading
            level={2}
            bottom={16}
            color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
            {staffToView?.name ?? ''} {reportDate}
          </Heading>
          <Paragraph
            level={2}
            color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
            {i18n('Report.NoSales')}
          </Paragraph>
        </Box>
      ) : reportData ? (
        <ReportView title={reportTitle} subTitle={reportDate} rows={reportData} />
      ) : (
        <Box></Box>
      )}
    </Wrapper>
  );
};

export default Report;
